import type { CharacterState, Rules } from "@/data/types";
import { calcDerivedStats } from "@/lib/characterCalc";
import { buildShareUrl } from "@/lib/characterUrl";
import { getValidationIssues } from "@/lib/characterValidation";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Copy,
  Check,
  Edit,
  Heart,
  Zap,
  Coins,
  Shield,
  Scroll,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Crown,
} from "lucide-react";
import { useState } from "react";
import {
  UltimateEligibleBanner,
  UltimateWarlockPanel,
} from "./UltimateWarlock";

interface CharacterSummaryProps {
  state: CharacterState;
  rules: Rules;
  onEdit: () => void;
  onChange: (s: CharacterState) => void;
}

export function CharacterSummary({
  state,
  rules,
  onEdit,
  onChange,
}: CharacterSummaryProps) {
  const stats = calcDerivedStats(state, rules);
  const [copied, setCopied] = useState(false);
  const [pactsExpanded, setPactsExpanded] = useState(false);
  const [showUltimateChecklist, setShowUltimateChecklist] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(buildShareUrl(state)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const allPacts = [...state.pacts, ...(state.bonusPacts || [])];
  const validationIssues = getValidationIssues(state, stats, rules);

  // ── Effective offering computation ──────────────────────────────────────────
  const swapMap = new Map<number, number>();
  for (const pact of allPacts) {
    if (
      pact.giftId === "gift-of-choice" &&
      pact.choiceSwapFromId &&
      pact.choiceSwapToId
    ) {
      swapMap.set(pact.choiceSwapFromId, pact.choiceSwapToId);
    }
  }
  for (const sg of state.standaloneGifts || []) {
    if (
      sg.giftId === "gift-of-choice" &&
      sg.choiceSwapFromId &&
      sg.choiceSwapToId
    ) {
      swapMap.set(sg.choiceSwapFromId, sg.choiceSwapToId);
    }
  }
  // Count-based won-back (mirrors characterCalc.ts logic — same offering can be won back multiple times)
  const wonBackCounts = new Map<number, number>();
  for (const id of state.wonBackOfferings || [])
    wonBackCounts.set(id, (wonBackCounts.get(id) || 0) + 1);
  const consumeWonBack = (id: number) => {
    const c = wonBackCounts.get(id) || 0;
    if (c > 0) {
      wonBackCounts.set(id, c - 1);
      return true;
    }
    return false;
  };

  type OfferingEntry = {
    offering: (typeof rules.darkOfferings)[number];
    originalOffering?: (typeof rules.darkOfferings)[number]; // set when swapped via Gift of Choice
    patronId?: string;
    source: "pact" | "forced" | "resurrection";
  };
  const activeOfferings: OfferingEntry[] = [];
  for (const pact of allPacts) {
    const effectiveId = swapMap.get(pact.darkOfferingId) ?? pact.darkOfferingId;
    if (consumeWonBack(effectiveId)) continue;
    const offering = rules.darkOfferings.find((o) => o.id === effectiveId);
    if (!offering) continue;
    const wasSwapped = effectiveId !== pact.darkOfferingId;
    const originalOffering = wasSwapped
      ? rules.darkOfferings.find((o) => o.id === pact.darkOfferingId)
      : undefined;
    activeOfferings.push({ offering, originalOffering, source: "pact" });
  }
  for (const offeringId of state.standaloneOfferings || []) {
    if (consumeWonBack(offeringId)) continue;
    const offering = rules.darkOfferings.find((o) => o.id === offeringId);
    if (offering) activeOfferings.push({ offering, source: "forced" });
  }
  for (const rp of state.resurrectionPacts || []) {
    if (consumeWonBack(rp.darkOfferingId)) continue;
    const offering = rules.darkOfferings.find(
      (o) => o.id === rp.darkOfferingId
    );
    if (offering)
      activeOfferings.push({
        offering,
        patronId: rp.patronId,
        source: "resurrection",
      });
  }

  // ── All gifts for display ────────────────────────────────────────────────────
  type GiftEntry = {
    gift: (typeof rules.otherworldlyGifts)[number];
    giftSkillId?: string;
    choiceSwapFromId?: number;
    choiceSwapToId?: number;
    patronId?: string;
    source: "pact" | "mission" | "exchange";
  };
  const allGiftEntries: GiftEntry[] = [
    ...allPacts
      .filter((pact) => !!pact.giftId)
      .map((pact) => {
        const gift = rules.otherworldlyGifts.find((g) => g.id === pact.giftId);
        if (!gift) return null;
        return {
          gift,
          giftSkillId: pact.giftSkillId,
          choiceSwapFromId: pact.choiceSwapFromId,
          choiceSwapToId: pact.choiceSwapToId,
          patronId: pact.patronId,
          source: "pact" as const,
        };
      })
      .filter((e): e is GiftEntry => e !== null),
    ...(state.standaloneGifts || [])
      .map((sg) => {
        const gift = rules.otherworldlyGifts.find((g) => g.id === sg.giftId);
        if (!gift) return null;
        return {
          gift,
          giftSkillId: sg.giftSkillId,
          choiceSwapFromId: sg.choiceSwapFromId,
          choiceSwapToId: sg.choiceSwapToId,
          patronId: sg.patronId,
          source: (sg.source === "exchange" ? "exchange" : "mission") as const,
        };
      })
      .filter((e): e is GiftEntry => e !== null),
  ];

  // ── Derived display values ────────────────────────────────────────────────────
  const wealth = rules.wealthLevels[stats.wealthLevel];
  const wealthName = wealth?.name ?? "Unknown";
  const startingCoin = wealth?.startingCoin ?? "?";
  const armour = wealth?.armour ?? { name: "None", ap: 0, dr: 0 };
  const hasArmourTraining = state.coreSkillSelections.some(
    (s) => s.skillId === "armour-training"
  );

  const spellsByList = rules.spellLists
    .map((list) => ({
      list,
      spells: list.spells.filter(
        (s) => stats.knownSpells.includes(s.id) && !s.deprecated
      ),
    }))
    .filter((g) => g.spells.length > 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">
            {state.name || "Unnamed Warlock"}
          </h1>
          {state.background && (
            <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
              {state.background}
            </p>
          )}
          <div className="flex flex-wrap gap-1.5 mt-2">
            <Badge variant="outline" className="text-xs">
              {rules.metadata.label} rules
            </Badge>
            {stats.isUltimateWarlock && (
              <Badge className="text-xs">Ultimate Warlock</Badge>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <Button variant="outline" onClick={onEdit}>
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button onClick={handleCopy}>
            {copied ? (
              <Check className="h-4 w-4 mr-2" />
            ) : (
              <Copy className="h-4 w-4 mr-2" />
            )}
            {copied ? "Copied!" : "Share"}
          </Button>
        </div>
      </div>

      {/* Validation banner */}
      {validationIssues.length > 0 && (
        <div
          className={`rounded-lg border px-4 py-3 flex gap-3 ${
            validationIssues.some((i) => i.severity === "error")
              ? "border-destructive/50 bg-destructive/10 text-destructive"
              : "border-amber-500/50 bg-amber-500/10 text-amber-700 dark:text-amber-400"
          }`}
        >
          <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <p className="text-sm font-semibold">Character is incomplete</p>
            <ul className="space-y-0.5">
              {validationIssues.map((issue, i) => (
                <li key={i} className="text-sm flex items-start gap-1.5">
                  <span className="shrink-0">•</span>
                  <span>{issue.message}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      <UltimateEligibleBanner state={state} onChange={onChange} />

      {/* Core Stats */}
      <div
        className={`grid gap-3 ${hasArmourTraining ? "grid-cols-2 sm:grid-cols-4" : "grid-cols-3"}`}
      >
        <Card>
          <CardContent className="pt-4 pb-4 flex flex-col items-center gap-1">
            <Heart className="h-5 w-5 text-red-500" />
            <span className="text-2xl font-bold">{stats.hp}</span>
            <span className="text-xs text-muted-foreground">
              HP (max {stats.maxHp})
            </span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 flex flex-col items-center gap-1">
            <Zap className="h-5 w-5 text-blue-500" />
            <span className="text-2xl font-bold">{stats.mp}</span>
            <span className="text-xs text-muted-foreground">MP</span>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4 pb-4 flex flex-col items-center gap-1">
            <Coins className="h-5 w-5 text-yellow-500" />
            <span className="text-2xl font-bold">{wealthName}</span>
            <span className="text-xs text-muted-foreground">
              {startingCoin}
            </span>
          </CardContent>
        </Card>
        {hasArmourTraining && (
          <Card>
            <CardContent className="pt-4 pb-4 flex flex-col items-center gap-1">
              <Shield className="h-5 w-5 text-green-500" />
              <span className="text-2xl font-bold text-center">
                {armour.name}
              </span>
              <span className="text-xs text-muted-foreground">
                {armour.ap > 0 ? `${armour.ap} AP` : "No armour"}
                {armour.dr > 0 ? `, ${armour.dr} DR` : ""}
              </span>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Core Skills + Dark Offerings side-by-side */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Core Skills */}
        {state.coreSkillSelections.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">Core Skills</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {state.coreSkillSelections.map((sel) => {
                const skill = rules.coreSkills.find(
                  (s) => s.id === sel.skillId
                );
                if (!skill) return null;
                return (
                  <div
                    key={sel.skillId}
                    className="flex items-start justify-between gap-2"
                  >
                    <div>
                      <span className="font-medium text-sm">{skill.name}</span>
                      {sel.count > 1 && (
                        <Badge variant="secondary" className="ml-2 text-xs">
                          ×{sel.count}
                        </Badge>
                      )}
                    </div>
                  </div>
                );
              })}
              <div className="text-xs text-muted-foreground pt-1 border-t">
                {stats.usedSkillPoints} / {stats.totalSkillPoints} skill points
                used
              </div>
            </CardContent>
          </Card>
        )}

        {/* Dark Offerings */}
        {activeOfferings.length > 0 && (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Dark Offerings ({activeOfferings.length}/
                {stats.darkOfferingsLimit})
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {activeOfferings.map((entry, i) => (
                <div
                  key={i}
                  className="rounded border border-destructive/25 bg-destructive/5 px-2.5 py-2"
                >
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-medium text-sm">
                      {entry.offering.name}
                    </span>
                    {entry.source === "forced" && (
                      <Badge variant="destructive" className="text-xs">
                        Forced
                      </Badge>
                    )}
                    {entry.source === "resurrection" && (
                      <Badge variant="destructive" className="text-xs">
                        Resurrection
                      </Badge>
                    )}
                    {entry.source === "resurrection" && entry.patronId && (
                      <Badge variant="outline" className="text-xs">
                        {rules.patrons.find((p) => p.id === entry.patronId)
                          ?.name ?? entry.patronId}
                      </Badge>
                    )}
                    {entry.originalOffering && (
                      <span className="text-xs text-muted-foreground italic">
                        (swapped from {entry.originalOffering.name})
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 leading-snug">
                    {entry.offering.description}
                  </p>
                </div>
              ))}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pacts — creation-time triplets, collapsible */}
      {allPacts.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">
                Pacts ({allPacts.length})
              </CardTitle>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs gap-1"
                onClick={() => setPactsExpanded((v) => !v)}
              >
                {pactsExpanded ? (
                  <ChevronUp className="h-3.5 w-3.5" />
                ) : (
                  <ChevronDown className="h-3.5 w-3.5" />
                )}
                {pactsExpanded ? "Collapse" : "Details"}
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {allPacts.map((pact, i) => {
              const originalOffering = rules.darkOfferings.find(
                (o) => o.id === pact.darkOfferingId
              );
              const effectiveId =
                swapMap.get(pact.darkOfferingId) ?? pact.darkOfferingId;
              const effectiveOffering = rules.darkOfferings.find(
                (o) => o.id === effectiveId
              );
              const wasSwapped = effectiveId !== pact.darkOfferingId;
              const isWonBack = (state.wonBackOfferings || []).includes(
                effectiveId
              );
              const patron = rules.patrons.find((p) => p.id === pact.patronId);
              const gift = rules.otherworldlyGifts.find(
                (g) => g.id === pact.giftId
              );
              const giftSkill = rules.coreSkills.find(
                (s) => s.id === pact.giftSkillId
              );
              const swapFrom = rules.darkOfferings.find(
                (o) => o.id === pact.choiceSwapFromId
              );
              const swapTo = rules.darkOfferings.find(
                (o) => o.id === pact.choiceSwapToId
              );
              return (
                <div
                  key={i}
                  className={`text-sm border rounded-md p-2 space-y-2 ${isWonBack ? "opacity-60" : ""}`}
                >
                  {/* Badge row */}
                  <div className="flex flex-wrap gap-1">
                    {originalOffering && (
                      <Badge variant="destructive" className="text-xs">
                        {wasSwapped ? (
                          <>
                            <span className="line-through opacity-60 mr-1">
                              {originalOffering.name}
                            </span>
                            → {effectiveOffering?.name}
                          </>
                        ) : (
                          originalOffering.name
                        )}
                      </Badge>
                    )}
                    {patron && (
                      <Badge variant="outline" className="text-xs">
                        {patron.name}
                      </Badge>
                    )}
                    {gift && (
                      <Badge variant="secondary" className="text-xs">
                        {gift.name}
                      </Badge>
                    )}
                    {isWonBack && (
                      <Badge
                        variant="outline"
                        className="text-xs text-muted-foreground"
                      >
                        Offering Freed
                      </Badge>
                    )}
                  </div>

                  {/* Expanded details */}
                  {pactsExpanded && (
                    <div className="space-y-2 pt-1">
                      {originalOffering && (
                        <div className="rounded bg-destructive/10 border border-destructive/20 p-2">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <p className="text-xs font-semibold text-destructive">
                              {originalOffering.name}
                            </p>
                            {wasSwapped && effectiveOffering && (
                              <p className="text-xs text-muted-foreground">
                                → swapped to{" "}
                                <span className="font-medium text-foreground">
                                  {effectiveOffering.name}
                                </span>
                              </p>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground leading-snug">
                            {originalOffering.description}
                          </p>
                          {wasSwapped && effectiveOffering && (
                            <p className="text-xs text-muted-foreground leading-snug mt-1 border-t pt-1 italic">
                              Effective curse: {effectiveOffering.description}
                            </p>
                          )}
                        </div>
                      )}
                      {patron && (
                        <div className="rounded border p-2">
                          <p className="text-xs font-semibold mb-1">
                            {patron.name}
                          </p>
                          <p className="text-xs text-muted-foreground leading-snug mb-1">
                            {patron.description}
                          </p>
                          <ul className="space-y-0.5">
                            {patron.challenges.map((c) => (
                              <li
                                key={c}
                                className="text-xs text-muted-foreground flex items-start gap-1"
                              >
                                <span className="shrink-0">•</span>
                                <span>{c}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {gift && (
                        <div className="rounded border border-primary/20 bg-primary/5 p-2">
                          <p className="text-xs font-semibold mb-0.5">
                            {gift.name}
                          </p>
                          <p className="text-xs text-muted-foreground leading-snug">
                            {gift.description}
                          </p>
                          {giftSkill && (
                            <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                              Core Skill:{" "}
                              <span className="font-medium">
                                {giftSkill.name}
                              </span>
                            </p>
                          )}
                          {gift.id === "gift-of-choice" &&
                            (swapFrom || swapTo) && (
                              <p className="text-xs text-violet-600 dark:text-violet-400 mt-1 flex items-center gap-1.5">
                                Swap:
                                {swapFrom ? (
                                  <span className="font-medium line-through text-muted-foreground">
                                    {swapFrom.name}
                                  </span>
                                ) : (
                                  <span className="italic text-muted-foreground">
                                    ?
                                  </span>
                                )}
                                <span>→</span>
                                {swapTo ? (
                                  <span className="font-medium">
                                    {swapTo.name}
                                  </span>
                                ) : (
                                  <span className="italic text-muted-foreground">
                                    ?
                                  </span>
                                )}
                              </p>
                            )}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </CardContent>
        </Card>
      )}

      {/* Otherworldly Gifts */}
      {allGiftEntries.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              Otherworldly Gifts ({allGiftEntries.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {allGiftEntries.map((entry, i) => {
                const patron = entry.patronId
                  ? rules.patrons.find((p) => p.id === entry.patronId)
                  : null;
                const giftSkill = entry.giftSkillId
                  ? rules.coreSkills.find((s) => s.id === entry.giftSkillId)
                  : null;
                const swapFromOff = entry.choiceSwapFromId
                  ? rules.darkOfferings.find(
                      (o) => o.id === entry.choiceSwapFromId
                    )
                  : null;
                const swapToOff = entry.choiceSwapToId
                  ? rules.darkOfferings.find(
                      (o) => o.id === entry.choiceSwapToId
                    )
                  : null;
                return (
                  <div
                    key={i}
                    className="rounded border border-primary/20 bg-primary/5 p-2.5"
                  >
                    <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                      <span className="font-medium text-sm">
                        {entry.gift.name}
                      </span>
                      {patron && (
                        <Badge variant="outline" className="text-xs">
                          {patron.name}
                        </Badge>
                      )}
                      {entry.source === "mission" && (
                        <Badge variant="secondary" className="text-xs">
                          Mission Reward
                        </Badge>
                      )}
                      {entry.source === "exchange" && (
                        <Badge variant="outline" className="text-xs">
                          Patron Exchange
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground leading-snug">
                      {entry.gift.description}
                    </p>
                    {giftSkill && (
                      <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                        Core Skill:{" "}
                        <span className="font-medium">{giftSkill.name}</span>
                      </p>
                    )}
                    {swapFromOff && swapToOff && (
                      <p className="text-xs text-violet-600 dark:text-violet-400 mt-1 flex items-center gap-1">
                        Swap:{" "}
                        <span className="line-through text-muted-foreground ml-0.5">
                          {swapFromOff.name}
                        </span>
                        <span className="mx-0.5">→</span>
                        <span className="font-medium">{swapToOff.name}</span>
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Spells */}
      {spellsByList.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <Scroll className="h-4 w-4" />
              Known Spells
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {spellsByList.map(({ list, spells }) => (
                <div key={list.id}>
                  <h4 className="font-medium text-sm mb-2 text-muted-foreground">
                    {list.name}
                  </h4>
                  <div className="space-y-2">
                    {spells.map((spell) => {
                      const points =
                        state.spellAllocations.find(
                          (a) => a.spellId === spell.id
                        )?.points ?? 0;
                      return (
                        <div
                          key={spell.id}
                          className="text-sm border rounded p-2"
                        >
                          <div className="flex items-center justify-between">
                            <span className="font-medium">{spell.name}</span>
                            {spell.scaling !== "binary" && points > 1 && (
                              <Badge variant="secondary" className="text-xs">
                                {points} pts
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {spell.duration} — {spell.effect}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Patron Challenges */}
      {stats.patronIds.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Patron Challenges</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {stats.patronIds.map((patronId) => {
                const patron = rules.patrons.find((p) => p.id === patronId);
                if (!patron) return null;
                return (
                  <div key={patronId} className="border rounded-md p-3">
                    <h4 className="font-medium text-sm mb-2 flex items-center gap-2">
                      {patron.name}
                      {patron.exiled && (
                        <Badge variant="outline" className="text-xs">
                          Previous Patron
                        </Badge>
                      )}
                    </h4>
                    <ul className="space-y-1">
                      {patron.challenges.map((challenge, i) => (
                        <li
                          key={i}
                          className="text-xs text-muted-foreground flex gap-1.5"
                        >
                          <span className="text-primary mt-0.5">•</span>
                          {challenge}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ultimate Warlock checklist */}
      <div className="space-y-3">
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowUltimateChecklist((v) => !v)}
        >
          <Crown className="h-4 w-4 mr-2" />
          {showUltimateChecklist ? "Hide" : "Show"} Ultimate Warlock
          requirements
        </Button>
        {showUltimateChecklist && (
          <Card>
            <CardContent className="pt-4">
              <UltimateWarlockPanel state={state} />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
